<template>
  <ModalAiConsent v-model:show="showConsentModal" @ai-terms-agreement-agreed="open" />
  <WdsTooltip bottom>
    <WdsButton secondary :disabled="isLoading" @click="handleButtonClick">
      <WdsStack align-center justify-center>
        <WdsIcon :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" :size="14" />
        <div class="m-l-5">{{ $t('Recap') }}</div>
      </WdsStack>
    </WdsButton>
    <template #tooltip-content>
      {{
        $t(
          'See what needs your attention in your recent conversations, and ask questions about them.',
        )
      }}
    </template>
  </WdsTooltip>
  <WdsModal :show="show" custom-size="min(720px, 95vw)" @cancel="show = false">
    <template #header>
      <WdsStack gap-sm align-center>
        <WdsIcon :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" />
        <div>
          <WdsHeading class="tw-my-0" type="title2">{{ $t('Conversation recap') }}</WdsHeading>
          <WdsText v-if="result && result.count > 0" small secondary data-test="scope">
            {{
              $t('Your :count most recent open conversations from the last 7 days', {
                count: result.count,
              })
            }}
          </WdsText>
        </div>
      </WdsStack>
    </template>

    <!-- Relative so the absolute sr-only texts are clipped here instead of making the modal scroll too. -->
    <div
      ref="scrollArea"
      class="tw-relative tw-max-h-[calc(100dvh-32rem)] tw-overflow-y-auto tw-overscroll-contain tw-pr-1"
      :class="
        questions.length > 0
          ? 'sm:tw-max-h-[calc(100dvh-25rem)]'
          : 'sm:tw-max-h-[calc(100dvh-29rem)]'
      "
    >
      <WdsStack
        v-if="isLoading"
        align-center
        justify-center
        gap-sm
        class="tw-py-8"
        data-test="loading"
      >
        <WdsSpinner indeterminate />
        <WdsText small>{{ $t('Reading your recent conversations…') }}</WdsText>
      </WdsStack>

      <template v-else-if="result">
        <WdsText v-if="result.count === 0" small secondary class="tw-pb-4" data-test="empty">
          {{
            $t(
              'No conversations with activity in the last 7 days. You can still ask about older ones below.',
            )
          }}
        </WdsText>

        <div
          v-if="result.count > 0"
          class="tw-pb-4 tw-border-b tw-border-gray-200"
          data-test="overview"
        >
          <WdsText>
            {{
              needsAttentionCount > 0
                ? $t(':count of :total conversations need something from you.', {
                    count: needsAttentionCount,
                    total: result.count,
                  })
                : $t('None of these conversations need anything from you.')
            }}
          </WdsText>
        </div>

        <ul class="tw-list-none tw-pl-0 tw-my-0">
          <li
            v-for="(conversation, index) in result.conversations"
            :key="index"
            class="tw-border-b tw-border-gray-100 last:tw-border-b-0"
            data-test="conversation"
          >
            <a
              :href="conversation.url"
              target="_blank"
              rel="noopener"
              class="tw-block tw-py-4 tw-text-inherit hover:tw-bg-gray-50 tw-no-underline"
              data-test="conversation-link"
            >
              <div class="tw-flex tw-gap-3">
                <WdsAvatar :initials="conversation.initials ?? ''" md class="tw-shrink-0" />
                <div class="tw-min-w-0 tw-flex-1">
                  <WdsStack align-center gap-sm wrap>
                    <WdsText class="tw-font-medium tw-break-words">
                      {{ conversation.title }}
                    </WdsText>
                    <WdsTag
                      v-if="conversation.awaiting_reply"
                      warning
                      sm
                      data-test="awaiting-reply"
                    >
                      {{ $t('Awaiting your reply') }}
                    </WdsTag>
                    <WdsTag v-if="conversation.unread" sm data-test="unread">
                      {{ $t('Unread') }}
                    </WdsTag>
                  </WdsStack>
                  <WdsText v-if="conversation.other_party" small secondary data-test="other-party">
                    {{ conversation.other_party }}
                  </WdsText>
                </div>
                <WdsStack align-center gap-xs class="tw-shrink-0 tw-self-start">
                  <WdsText v-if="conversation.time" small secondary>
                    {{ conversation.time }}
                  </WdsText>
                  <WdsIcon
                    :name="WdsIconNameEnum.CHEVRON_RIGHT"
                    :size="16"
                    class="tw-text-gray-400"
                  />
                </WdsStack>
              </div>
              <WdsText v-if="conversation.summary" class="tw-pt-3" data-test="conversation-summary">
                {{ conversation.summary }}
              </WdsText>
              <div
                v-if="conversation.actions.length > 0"
                class="tw-mt-3 tw-rounded-lg tw-bg-gray-50 tw-px-4 tw-py-3"
                data-test="actions"
              >
                <WdsText class="tw-font-medium">{{ $t('Suggested next steps') }}</WdsText>
                <WdsStack
                  v-for="(action, actionIndex) in conversation.actions"
                  :key="actionIndex"
                  align-center
                  gap-sm
                  class="tw-pt-1"
                  data-test="action"
                >
                  <WdsIcon
                    :name="WdsIconNameEnum.ARROW_RIGHT"
                    :size="14"
                    class="tw-shrink-0 tw-text-gray-400"
                  />
                  <WdsText secondary>{{ action }}</WdsText>
                </WdsStack>
              </div>
              <span class="tw-sr-only">{{ $t('(opens in a new tab)') }}</span>
            </a>
          </li>
        </ul>
      </template>

      <section
        v-if="questions.length > 0"
        class="tw-pt-4 tw-border-t tw-border-gray-200"
        data-test="ask"
      >
        <WdsStack align-center justify-space-between gap-sm class="tw-pb-2">
          <WdsText small class="tw-font-semibold tw-uppercase tw-tracking-wide">
            {{ $t('Ask about your conversations') }}
          </WdsText>
          <WdsButtonText :disabled="isAsking" data-test="start-new" @click="clearChat">
            {{ $t('Clear chat') }}
          </WdsButtonText>
        </WdsStack>

        <div
          v-for="(entry, index) in questions"
          :key="`qa-${index}`"
          class="tw-pt-3"
          data-test="qa"
        >
          <div class="tw-flex tw-justify-end">
            <div
              class="tw-max-w-[85%] tw-rounded-lg tw-bg-gray-100 tw-px-3 tw-py-2"
              data-test="question"
            >
              <WdsText small>{{ entry.question }}</WdsText>
            </div>
          </div>
          <WdsStack
            v-if="entry.answer === null"
            align-center
            gap-sm
            class="tw-pt-2"
            data-test="asking"
          >
            <WdsSpinner indeterminate />
            <WdsText small>{{ $t('Searching your conversations…') }}</WdsText>
          </WdsStack>
          <div v-else class="tw-flex tw-gap-2 tw-pt-2" data-test="answer">
            <WdsIcon
              :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT"
              :size="16"
              class="tw-shrink-0 tw-mt-1"
            />
            <div class="tw-min-w-0">
              <template v-for="(block, blockIndex) in answerBlocks(entry)" :key="blockIndex">
                <ul v-if="block.items" class="tw-list-disc tw-pl-5 tw-my-0">
                  <li v-for="(item, itemIndex) in block.items" :key="itemIndex" class="tw-py-1">
                    <WdsText small>
                      <template v-for="(seg, segIndex) in item" :key="segIndex">
                        <a
                          v-if="seg.url"
                          :href="seg.url"
                          target="_blank"
                          rel="noopener"
                          data-test="answer-link"
                        >
                          {{ seg.text }}
                          <span class="tw-sr-only">{{ $t('(opens in a new tab)') }}</span>
                        </a>
                        <template v-else>{{ seg.text }}</template>
                      </template>
                    </WdsText>
                  </li>
                </ul>
                <WdsText v-else small class="tw-py-1">
                  <template v-for="(seg, segIndex) in block.parts" :key="segIndex">
                    <a
                      v-if="seg.url"
                      :href="seg.url"
                      target="_blank"
                      rel="noopener"
                      data-test="answer-link"
                    >
                      {{ seg.text }}
                      <span class="tw-sr-only">{{ $t('(opens in a new tab)') }}</span>
                    </a>
                    <template v-else>{{ seg.text }}</template>
                  </template>
                </WdsText>
              </template>
            </div>
          </div>
        </div>
      </section>
      <!-- Spacer: at the end of the list the fade covers it, not the last line. -->
      <div class="tw-h-6" />
      <!-- Fades the content under it while there is more to scroll. -->
      <div
        class="tw-pointer-events-none tw-sticky tw-bottom-0 -tw-mt-6 tw-h-6 tw-bg-gradient-to-t tw-from-white"
      />
    </div>

    <template v-if="result" #footer>
      <form class="tw-pt-3 tw-border-t tw-border-gray-200" @submit.prevent="ask(question)">
        <!-- One row that scrolls sideways, so long or translated questions never push the footer off screen. -->
        <WdsStack
          v-if="questions.length === 0"
          gap-sm
          class="tw-pb-3 sm:tw-overflow-x-auto"
          data-test="suggestions"
        >
          <!-- Phones show one suggestion so the input and the AI label stay on screen. -->
          <!-- The wrapper hides it: WdsButton's own display rule wins over tw-hidden. -->
          <div
            v-for="(suggestion, index) in suggestions"
            :key="suggestion"
            class="sm:tw-shrink-0 sm:tw-whitespace-nowrap"
            :class="{ 'tw-hidden sm:tw-block': index > 0 }"
          >
            <WdsButton
              type="button"
              secondary
              sm
              :disabled="isAsking"
              data-test="suggestion"
              @click="ask(suggestion)"
            >
              {{ suggestion }}
            </WdsButton>
          </div>
        </WdsStack>
        <WdsStack align-center gap-sm>
          <!-- WdsInput forwards class to its <input>, so the wrapper sets the width. -->
          <div ref="inputWrapper" class="tw-flex-1 tw-min-w-0">
            <WdsInput
              v-model="question"
              class="!tw-m-0"
              maxlength="500"
              :placeholder="$t('Ask a question…')"
              :disabled="isAsking || turnCapReached"
              data-test="question-input"
            />
          </div>
          <WdsButton type="submit" :disabled="isAsking || turnCapReached || !question.trim()">
            {{ $t('Send') }}
          </WdsButton>
        </WdsStack>
        <WdsText v-if="turnCapReached" small secondary class="tw-pt-1" data-test="turn-cap">
          {{ $t('This chat is full. Clear the chat to ask a new question.') }}
        </WdsText>
        <WdsText v-if="askFailed" small error class="tw-pt-1" data-test="ask-error">
          {{ $t('Something went wrong. Please try again.') }}
        </WdsText>
      </form>
      <WdsStack align-center justify-space-between gap-sm wrap class="tw-pt-3">
        <WdsText small secondary data-test="ai-label">
          {{
            $t(
              'Generated by Worksome Intelligence. It can make mistakes. Check the original conversations.',
            )
          }}
          <a :href="aiHelpArticleUrl" target="_blank" rel="noopener">
            {{ $t('how Worksome uses AI') }}
          </a>
        </WdsText>
        <WdsStack v-if="generatedAt" align-center gap-sm class="tw-shrink-0">
          <WdsText small secondary data-test="generated-at">
            {{ $t('Updated :time', { time: formatTime(generatedAt) }) }}
          </WdsText>
          <WdsButtonText :disabled="isLoading" data-test="refresh" @click="loadRecap">
            {{ $t('Refresh') }}
          </WdsButtonText>
        </WdsStack>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import {
  WdsAvatar,
  WdsButton,
  WdsButtonText,
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
  WdsInput,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsTag,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import axios from 'axios'
import { computed, nextTick, ref } from 'vue'
import { useTranslation } from '@/composables/translation'
import ModalAiConsent from '@/components/LLM/ModalAiConsent.vue'
import { useAiConsent } from '@/composables/useAiConsent'
import { useToast } from '@/composables/useToast'
import api from '@/api/api-ai'

type Conversation = {
  title: string
  url: string
  other_party: string | null
  initials: string | null
  time: string | null
  summary: string
  actions: string[]
  awaiting_reply: boolean
  unread: boolean
}

type ConversationRecap = {
  conversations: Conversation[]
  count: number
  suggested_questions: string[]
}

type AnswerPart = { text: string; url: string | null }

type AnswerBlock = { items: AnswerPart[][]; parts?: never } | { parts: AnswerPart[]; items?: never }

type QuestionEntry = {
  question: string
  answer: string | null
  refs: Record<string, { title: string; url: string }>
}

const aiHelpArticleUrl =
  'https://help.worksome.dk/en/articles/12107614-worksome-intelligence-smarter-simpler-and-built-for-you'

const { $t } = useTranslation()
const toast = useToast()
const { needsConsent, showConsentModal } = useAiConsent()

const show = ref(false)
const isLoading = ref(false)
const result = ref<ConversationRecap | null>(null)
const generatedAt = ref<Date | null>(null)
const question = ref('')
const questions = ref<QuestionEntry[]>([])
const threadUuid = ref<string | null>(null)
const isAsking = ref(false)
const turnCapReached = ref(false)
const askFailed = ref(false)
const scrollArea = ref<HTMLElement | null>(null)
const inputWrapper = ref<HTMLElement | null>(null)

const fallbackSuggestions = [
  $t('Which conversations mention a start date?'),
  $t('What is still open from last month?'),
  $t('Which messages mention invoices?'),
]

const suggestions = computed(() =>
  [...new Set([...(result.value?.suggested_questions ?? []), ...fallbackSuggestions])].slice(0, 3),
)

const needsAttentionCount = computed(
  () =>
    (result.value?.conversations ?? []).filter(
      (conversation) => conversation.actions.length > 0 || conversation.awaiting_reply,
    ).length,
)

const formatTime = (date: Date) =>
  date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

const scrollToBottom = async () => {
  await nextTick()

  if (scrollArea.value) {
    scrollArea.value.scrollTop = scrollArea.value.scrollHeight
  }
}

// Phones skip the focus so the keyboard does not cover the recap.
const focusInput = async () => {
  await nextTick()

  if (window.matchMedia?.('(pointer: fine)').matches !== false) {
    inputWrapper.value?.querySelector('input')?.focus()
  }
}

const clearChat = () => {
  threadUuid.value = null
  questions.value = []
  turnCapReached.value = false
  askFailed.value = false
  focusInput()
}

const lineParts = (line: string, refs: QuestionEntry['refs']): AnswerPart[] =>
  line
    .replaceAll('**', '')
    .split(/\[#(\d+)\]/)
    .map((text, index): AnswerPart | null => {
      if (index % 2 === 0) {
        return { text, url: null }
      }

      const target = refs[text]

      return target ? { text: target.title, url: target.url } : null
    })
    .filter((part): part is AnswerPart => part !== null && part.text !== '')
    .map((part, index, parts) =>
      // A line that starts with a link reads as "Title — text".
      index === 1 && parts[0].url && /^\s*[\p{L}\d]/u.test(part.text)
        ? { ...part, text: ` — ${part.text.trimStart()}` }
        : part,
    )

const answerBlocks = (entry: QuestionEntry): AnswerBlock[] =>
  (entry.answer ?? '').split('\n').reduce<AnswerBlock[]>((blocks, rawLine) => {
    const line = rawLine.trim()

    if (line === '') {
      return blocks
    }

    if (!line.startsWith('- ')) {
      return [...blocks, { parts: lineParts(line, entry.refs) }]
    }

    const item = lineParts(line.slice(2), entry.refs)
    const last = blocks.at(-1)

    if (last?.items) {
      last.items.push(item)

      return blocks
    }

    return [...blocks, { items: [item] }]
  }, [])

const ask = async (input: string) => {
  const text = input.trim()

  if (!text || isAsking.value || turnCapReached.value) return

  questions.value.push({ question: text, answer: null, refs: {} })
  const entry = questions.value[questions.value.length - 1]
  question.value = ''
  isAsking.value = true
  askFailed.value = false
  scrollToBottom()

  try {
    const response = await api.askConversations(text, threadUuid.value)
    entry.answer = response.data.answer
    entry.refs = response.data.refs
    threadUuid.value = response.data.ai_thread_uuid
  } catch (error) {
    questions.value = questions.value.filter((item) => item !== entry)

    if (axios.isAxiosError(error) && error.response?.status === 422 && threadUuid.value) {
      turnCapReached.value = true
    } else {
      askFailed.value = true
      question.value = text
    }
  } finally {
    isAsking.value = false
    scrollToBottom()
  }
}

const loadRecap = async () => {
  isLoading.value = true

  try {
    const response = await api.recapConversations()
    result.value = response.data
    generatedAt.value = new Date()
    focusInput()
  } catch {
    if (!result.value) {
      show.value = false
    }
    toast.error($t('An error occurred, please try again.'))
  } finally {
    isLoading.value = false
  }
}

const open = () => {
  show.value = true

  if (!result.value) {
    loadRecap()
  } else {
    focusInput()
  }
}

const handleButtonClick = () => {
  if (needsConsent.value) {
    showConsentModal.value = true
  } else {
    open()
  }
}
</script>
